import React, { useState, useEffect, useContext, useMemo } from 'react';
import { UserDetailsContext } from 'context/userDetailsContext';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';
import { getPageLimit, pickExtractedDate, getFilePathForPagination } from './helpers/operatingAccountingHelpersV2';
import { handlePageNumberInPagination } from 'common/pagination';
import { getOperatingAccountBalance } from 'services/billingServices';
import { useQuery } from '@tanstack/react-query';
import OperatingAccountingFiltersV2 from './components/OperatingAccountingFiltersV2/OperatingAccountingFiltersV2';
import {
  caseNameBody,
  dateBody,
  paidFromBody,
  paymentTypeBody,
  amountBody,
  paidToBody,
  rowClassName,
} from './helpers/operatingAccountingDataTableHelpersV2';
import { useTrustAccountListV2Query } from '../hooks/queries';

const OperatingAccountingV2 = ({ caseDetails, caseView, isActiveTab = true, setActiveIndex, temporaryFundsTabIndex }) => {
  const userContext = useContext(UserDetailsContext);
  const [dateFilter, setDateFilter] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedSourceAccounts, setSelectedSourceAccounts] = useState([]);
  const [selectedDestinationAccounts, setSelectedDestinationAccounts] = useState([]);
  const [pageLimit, setPageLimit] = useState(getPageLimit(false, caseView, userContext) ?? 10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  // Map DataTable field names to API sort_by field names
  const getCurrentSortField = useMemo(() => {
    if (!sortField) return ''; // Return empty string when no sort field selected
    const fieldMap = {
      record_created_date: 'record_created_date',
      time: 'time',
      amount: 'amount',
      case_name: 'case_name',
      contact_name: 'contact_name',
      entry_type: 'entry_type',
      payment_type: 'payment_type',
      paid_from: 'paid_from',
      paid_to: 'paid_to',
    };
    return fieldMap[sortField] || '';
  }, [sortField]);

  const getCurrentSortOrder = useMemo(() => {
    if (sortOrder === null || sortOrder === undefined) return ''; // Return empty string when no sort order selected
    return sortOrder === 1 ? 'asc' : 'desc';
  }, [sortOrder]);

  // Extract date range from calendar
  const dateRange = useMemo(() => {
    return pickExtractedDate(dateFilter, userContext?.userDetails?.timezone);
  }, [dateFilter, userContext?.userDetails?.timezone]);

  // Normalize filter values for consistent query keys (empty strings -> null)
  const normalizedFilters = useMemo(() => {
    return {
      transaction_date_lower: dateRange.transaction_date_lower || null,
      transaction_date_upper: dateRange.transaction_date_upper || null,
    };
  }, [dateRange]);

  // React Query hook for fetching operating account transactions
  const { transactions, totalRecords, totalPages, isLoading } = useTrustAccountListV2Query({
    limit: pageLimit,
    page: currentPage,
    case_id: caseView ? caseDetails?.case_id : selectedAccount ? null : null,
    account_id: selectedAccount || (caseView && caseDetails?.default_operating_account?.[0]?.account_id) || null,
    account_type: 'operating',
    transaction_date_lower: normalizedFilters.transaction_date_lower,
    transaction_date_upper: normalizedFilters.transaction_date_upper,
    source_account_id: selectedSourceAccounts && selectedSourceAccounts.length > 0 ? selectedSourceAccounts : null,
    destination_account_id: selectedDestinationAccounts && selectedDestinationAccounts.length > 0 ? selectedDestinationAccounts : null,
    sort_by: getCurrentSortField,
    sort_order: getCurrentSortOrder,
    enabled: isActiveTab,
  });

  // React Query for operating account balance
  const accountIdForBalance = selectedAccount || (caseView && caseDetails?.default_operating_account?.[0]?.account_id);
  const { data: balanceData } = useQuery({
    queryKey: ['operating-account-balance', accountIdForBalance, caseDetails?.case_id],
    queryFn: () => getOperatingAccountBalance(accountIdForBalance, caseView ? caseDetails?.case_id : null),
    enabled: !!accountIdForBalance && isActiveTab,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const operatingBalance = balanceData?.data?.available_balance ? parseFloat(balanceData.data.available_balance) : 0;

  useEffect(() => {
    if (caseView && caseDetails?.default_operating_account?.[0]?.account_id) {
      setSelectedAccount(caseDetails?.default_operating_account?.[0]?.account_id);
    }
  }, [caseView, caseDetails?.default_operating_account]);

  const onPageChange = (page) => {
    setCurrentPage(page);
    const path = getFilePathForPagination(false, caseView);
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, page);
  };

  const onPageSizeChange = (newPageSize) => {
    setPageLimit(newPageSize);
    setCurrentPage(1);
    const path = getFilePathForPagination(false, caseView);
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, newPageSize);
  };

  const onSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setCurrentPage(1);
  };

  const handleAccountChange = (value) => {
    setSelectedAccount(value);
    setCurrentPage(1);
  };

  const handleSourceAccountsChange = (value) => {
    setSelectedSourceAccounts(value);
    setCurrentPage(1);
  };

  const handleDestinationAccountsChange = (value) => {
    setSelectedDestinationAccounts(value);
    setCurrentPage(1);
  };

  const refreshData = () => {
    setDateFilter(null);
    setSelectedAccount(caseView ? caseDetails?.default_operating_account?.[0]?.account_id : null);
    setSelectedSourceAccounts([]);
    setSelectedDestinationAccounts([]);
    setCurrentPage(1);
    setSortField(null);
    setSortOrder(null);
    // Don't call refetch() - React Query will automatically use cached data if available
    // The query will automatically re-run when state changes, and if the query key matches
    // a previous query with cached data (within staleTime), it will use the cache
  };

  return (
    <>
      <OperatingAccountingFiltersV2
        dateFilter={dateFilter}
        onDateFilterChange={handleDateFilterChange}
        selectedAccount={selectedAccount}
        onAccountChange={handleAccountChange}
        selectedSourceAccounts={selectedSourceAccounts}
        onSourceAccountsChange={handleSourceAccountsChange}
        selectedDestinationAccounts={selectedDestinationAccounts}
        onDestinationAccountsChange={handleDestinationAccountsChange}
        refreshData={refreshData}
        isFromCaseSpecificModule={caseView}
        operatingBalance={operatingBalance}
      />

      <div className="datatable-responsive bg-white shadow-middle mb-3">
        <ResizableDataTable
          value={transactions || []}
          className="p-datatable-responsive"
          emptyMessage={isLoading ? 'Loading...' : 'No records found'}
          rowClassName={rowClassName}
          paginator={false}
          rows={pageLimit}
          storageKey="operating_accounting_column_widths"
          sortMode="server"
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          columns={[
            {
              field: 'case_name',
              header: 'Case Name',
              body: (rowData) => caseNameBody({ rowData, hidden: caseView }),
              bodyClassName: 'ellipsis-text',
              hidden: caseView,
              sortable: true,
            },
            {
              field: 'record_created_date',
              header: 'Date of Transaction',
              body: (rowData) => dateBody({ rowData, userContext }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'paid_from',
              header: 'Paid From',
              body: (rowData) => paidFromBody({ rowData }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'paid_to',
              header: 'Paid To',
              body: (rowData) => paidToBody({ rowData }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'payment_type',
              header: 'Payment Type',
              body: (rowData) => paymentTypeBody({ rowData }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'amount',
              header: 'Amount',
              body: (rowData) => amountBody({ rowData }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
          ]}
        />

        <EllipsisPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          pageSize={pageLimit}
          totalRecords={totalRecords}
          onPageSizeChange={onPageSizeChange}
          showPageSizeDropdown={true}
        />
      </div>
    </>
  );
};

export default OperatingAccountingV2;
